// Skill Progression Engine
// Manages skill progression, word selection, and mastery tracking

import { supabase } from '@/lib/supabase/client';
import type {
  CurrentSkill,
  ChildSkillProgress,
  PracticeWord,
  SessionWordSelection,
  SkillSequenceRecord,
} from './types';

// =============================================================================
// Constants
// =============================================================================

const WORDS_PER_SESSION = 3;
const TARGET_PRODUCTIONS_PER_WORD = 3;
const TARGET_EXPOSURES_PER_WORD = 10;
const DEFAULT_MASTERY_THRESHOLD = 80; // 80% of words mastered

// =============================================================================
// Skill Sequence Functions
// =============================================================================

/**
 * Get the full skill sequence in order
 */
export async function getSkillSequence(): Promise<SkillSequenceRecord[]> {
  const { data, error } = await supabase
    .from('skill_sequence')
    .select('id, skill_id, sequence_order, phase, sound_pattern, color, display_name, description, intro_audio_url, intro_script, mastery_threshold, words_per_skill')
    .order('sequence_order', { ascending: true });

  if (error) {
    console.error('Error fetching skill sequence:', error);
    throw new Error(`Failed to fetch skill sequence: ${error.message}`);
  }

  // Cast phase type from database string to our union type
  return (data || []).map(row => ({
    ...row,
    phase: row.phase as '1' | '2' | '3' | 'r',
  }));
}

/**
 * Get a specific skill by ID
 */
export async function getSkillById(skillId: string): Promise<SkillSequenceRecord | null> {
  const { data, error } = await supabase
    .from('skill_sequence')
    .select('id, skill_id, sequence_order, phase, sound_pattern, color, display_name, description, intro_audio_url, intro_script, mastery_threshold, words_per_skill')
    .eq('skill_id', skillId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching skill:', error);
    throw new Error(`Failed to fetch skill: ${error.message}`);
  }

  if (!data) return null;

  // Cast phase type from database string to our union type
  return {
    ...data,
    phase: data.phase as '1' | '2' | '3' | 'r',
  };
}

// =============================================================================
// Child Skill Progress Functions
// =============================================================================

/**
 * Get the current learning skill for a child
 * Returns null if no skill is in 'learning' status
 */
export async function getCurrentLearningSkill(childId: string): Promise<CurrentSkill | null> {
  const { data, error } = await supabase
    .from('child_skill_progress')
    .select(`
      skill_id,
      skill_sequence (
        sequence_order,
        display_name,
        sound_pattern,
        color,
        intro_audio_url,
        phase,
        mastery_threshold
      )
    `)
    .eq('child_id', childId)
    .eq('status', 'learning')
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Error fetching current skill:', error);
    throw new Error(`Failed to fetch current skill: ${error.message}`);
  }

  if (!data || !data.skill_sequence) {
    return null;
  }

  const skill = data.skill_sequence as unknown as SkillSequenceRecord;
  return {
    skillId: data.skill_id,
    sequenceOrder: skill.sequence_order,
    soundPattern: skill.sound_pattern,
    color: skill.color,
    introAudioUrl: skill.intro_audio_url,
    phase: skill.phase as '1' | '2' | 'r',
    masteryThreshold: skill.mastery_threshold || DEFAULT_MASTERY_THRESHOLD,
  };
}

/**
 * Initialize skill progress for a new child
 * Sets the first skill in the sequence to 'learning'
 */
export async function initializeChildSkill(childId: string): Promise<CurrentSkill | null> {
  // Use the database function for atomic initialization
  const { data, error } = await supabase.rpc('initialize_child_skill', {
    p_child_id: childId,
  });

  if (error) {
    console.error('Error initializing child skill:', error);
    throw new Error(`Failed to initialize child skill: ${error.message}`);
  }

  // If we got a skill_id back, fetch the full skill info
  if (data) {
    return getCurrentLearningSkill(childId);
  }

  return null;
}

/**
 * Get all skill progress records for a child
 */
export async function getChildSkillProgress(childId: string): Promise<ChildSkillProgress[]> {
  const { data, error } = await supabase
    .from('child_skill_progress')
    .select('id, child_id, skill_id, status, started_at, mastered_at, words_practiced, words_mastered, total_sessions, total_exposures, total_productions')
    .eq('child_id', childId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching child skill progress:', error);
    throw new Error(`Failed to fetch child skill progress: ${error.message}`);
  }

  return (data || []).map(record => ({
    id: record.id,
    child_id: record.child_id,
    skill_id: record.skill_id,
    status: record.status as ChildSkillProgress['status'],
    started_at: record.started_at,
    mastered_at: record.mastered_at,
    words_practiced: record.words_practiced || 0,
    words_mastered: record.words_mastered || 0,
    total_sessions: record.total_sessions || 0,
    total_exposures: record.total_exposures || 0,
    total_productions: record.total_productions || 0,
  }));
}

/**
 * Advance child to the next skill in sequence
 * Marks current skill as mastered and sets next skill to learning
 */
export async function advanceToNextSkill(childId: string): Promise<CurrentSkill | null> {
  const { data, error } = await supabase.rpc('advance_to_next_skill', {
    p_child_id: childId,
  });

  if (error) {
    console.error('Error advancing to next skill:', error);
    throw new Error(`Failed to advance to next skill: ${error.message}`);
  }

  // If we got a skill_id back, fetch the full skill info
  if (data) {
    return getCurrentLearningSkill(childId);
  }

  // No more skills - child has completed all!
  return null;
}

// =============================================================================
// Word Selection Functions
// =============================================================================

/**
 * Get all words for a specific skill
 */
export async function getWordsForSkill(skillId: string): Promise<PracticeWord[]> {
  const { data, error } = await supabase
    .from('turn_color_words')
    .select('id, word, turn_code, skill_id')
    .eq('skill_id', skillId);

  if (error) {
    console.error('Error fetching words for skill:', error);
    throw new Error(`Failed to fetch words for skill: ${error.message}`);
  }

  return (data || []).map(word => ({
    id: word.id,
    word: word.word,
    turnCode: word.turn_code,
    skillId: word.skill_id || skillId,
    currentProductions: 0,
    targetProductions: TARGET_PRODUCTIONS_PER_WORD,
    isMastered: false,
  }));
}

/**
 * Get word mastery data for a child and skill
 */
async function getWordMasteryForSkill(
  childId: string,
  skillId: string
): Promise<Map<string, { productions: number; isMastered: boolean }>> {
  const { data, error } = await supabase
    .from('child_vocabulary_mastery')
    .select('word_id, phase1_successes, phase2_successes, overall_mastery_status')
    .eq('child_id', childId);

  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching word mastery:', error);
    // Don't throw - return empty map and continue
    return new Map();
  }

  const masteryMap = new Map<string, { productions: number; isMastered: boolean }>();
  
  for (const record of data || []) {
    const totalSuccesses = (record.phase1_successes || 0) + (record.phase2_successes || 0);
    masteryMap.set(record.word_id, {
      productions: totalSuccesses,
      isMastered: record.overall_mastery_status === 'mastered',
    });
  }

  return masteryMap;
}

/**
 * Select words for a session based on mastery progress
 * Prioritizes words that haven't been mastered yet
 */
export async function selectWordsForSession(
  childId: string,
  skillId: string,
  count: number = WORDS_PER_SESSION
): Promise<SessionWordSelection> {
  // Get the skill info
  const skill = await getSkillById(skillId);
  if (!skill) {
    throw new Error(`Skill not found: ${skillId}`);
  }

  // Get all words for this skill
  const allWords = await getWordsForSkill(skillId);
  
  // Get mastery data for this child
  const masteryData = await getWordMasteryForSkill(childId, skillId);

  // Enrich words with mastery data
  const enrichedWords = allWords.map(word => {
    const mastery = masteryData.get(word.id);
    return {
      ...word,
      currentProductions: mastery?.productions || 0,
      isMastered: mastery?.isMastered || false,
    };
  });

  // Sort by priority:
  // 1. Words with 0-2 productions (not yet mastered, need more practice)
  // 2. Words never practiced
  // 3. Random selection from remaining
  const sorted = enrichedWords.sort((a, b) => {
    // Mastered words go to the end
    if (a.isMastered && !b.isMastered) return 1;
    if (!a.isMastered && b.isMastered) return -1;
    
    // Prioritize words close to mastery (1-2 productions)
    const aCloseToMastery = a.currentProductions > 0 && a.currentProductions < TARGET_PRODUCTIONS_PER_WORD;
    const bCloseToMastery = b.currentProductions > 0 && b.currentProductions < TARGET_PRODUCTIONS_PER_WORD;
    if (aCloseToMastery && !bCloseToMastery) return -1;
    if (!aCloseToMastery && bCloseToMastery) return 1;
    
    // Then new words
    if (a.currentProductions === 0 && b.currentProductions > 0) return -1;
    if (a.currentProductions > 0 && b.currentProductions === 0) return 1;
    
    // Random among equals
    return Math.random() - 0.5;
  });

  // Select top N words
  const selectedWords = sorted.slice(0, count);

  return {
    skillId,
    soundPattern: skill.sound_pattern,
    color: skill.color,
    words: selectedWords,
  };
}

// =============================================================================
// Mastery Tracking Functions
// =============================================================================

/**
 * Record a word production (child said the word)
 */
export async function recordWordProduction(
  childId: string,
  wordId: string,
  successful: boolean,
  sessionId?: string
): Promise<void> {
  // First, get or create the mastery record
  // Use maybeSingle() since the record might not exist yet
  const { data: existing } = await supabase
    .from('child_vocabulary_mastery')
    .select('id, phase1_successes, phase1_attempts, total_sessions_used')
    .eq('child_id', childId)
    .eq('word_id', wordId)
    .maybeSingle();

  if (existing) {
    // Update existing record
    const updates: Record<string, unknown> = {
      phase1_attempts: (existing.phase1_attempts || 0) + 1,
      updated_at: new Date().toISOString(),
    };

    if (successful) {
      updates.phase1_successes = (existing.phase1_successes || 0) + 1;
      
      // Check if mastered (3+ successes)
      if ((existing.phase1_successes || 0) + 1 >= TARGET_PRODUCTIONS_PER_WORD) {
        updates.phase1_mastered = true;
        updates.phase1_mastered_at = new Date().toISOString();
        updates.overall_mastery_status = 'mastered';
      } else if ((existing.phase1_successes || 0) === 0) {
        updates.overall_mastery_status = 'learning';
      }
    }

    if (sessionId) {
      updates.last_session_id = sessionId;
      updates.total_sessions_used = (existing.total_sessions_used || 0) + 1;
    }

    await supabase
      .from('child_vocabulary_mastery')
      .update(updates)
      .eq('id', existing.id);
  } else {
    // Create new record
    await supabase
      .from('child_vocabulary_mastery')
      .insert({
        child_id: childId,
        word_id: wordId,
        phase1_attempts: 1,
        phase1_successes: successful ? 1 : 0,
        overall_mastery_status: successful ? 'learning' : 'new',
        last_session_id: sessionId,
        total_sessions_used: sessionId ? 1 : 0,
      });
  }
}

/**
 * Update skill progress after a session
 * 
 * NOTE: This function returns `shouldAdvance: boolean` but does NOT automatically
 * advance the child to the next skill. Auto-advancement is handled by the
 * `session-analyzer` edge function which calls `advanceToNextSkill()` when
 * `shouldAdvance` is true. Frontend callers should use this return value
 * for UI feedback only (e.g., showing a "skill mastered!" celebration).
 */
export async function updateSkillProgress(
  childId: string,
  skillId: string,
  sessionStats: {
    wordsExposed: number;
    wordsProduced: number;
    successfulProductions: number;
  }
): Promise<{ shouldAdvance: boolean }> {
  // Get current progress - use maybeSingle() since record might not exist yet
  const { data: progress, error } = await supabase
    .from('child_skill_progress')
    .select('id, total_sessions, total_exposures, total_productions')
    .eq('child_id', childId)
    .eq('skill_id', skillId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching skill progress:', error);
    throw new Error(`Failed to fetch skill progress: ${error.message}`);
  }

  if (!progress) {
    // Check if child already has a learning skill before inserting.
    // New skills default to 'not_started' — admin explicitly promotes via UI.
    const { data: hasLearning } = await supabase
      .from('child_skill_progress')
      .select('id')
      .eq('child_id', childId)
      .eq('status', 'learning')
      .maybeSingle();
    
    // If child already has a learning skill, insert as 'not_started'
    // Otherwise, this skill becomes the learning skill
    const status = hasLearning ? 'not_started' : 'learning';
    
    // Create new progress record
    await supabase
      .from('child_skill_progress')
      .insert({
        child_id: childId,
        skill_id: skillId,
        status,
        started_at: new Date().toISOString(),
        total_sessions: 1,
        total_exposures: sessionStats.wordsExposed,
        total_productions: sessionStats.successfulProductions,
      });
    return { shouldAdvance: false };
  }

  // Update progress
  const newTotalSessions = (progress.total_sessions || 0) + 1;
  const newTotalExposures = (progress.total_exposures || 0) + sessionStats.wordsExposed;
  const newTotalProductions = (progress.total_productions || 0) + sessionStats.successfulProductions;

  await supabase
    .from('child_skill_progress')
    .update({
      total_sessions: newTotalSessions,
      total_exposures: newTotalExposures,
      total_productions: newTotalProductions,
      updated_at: new Date().toISOString(),
    })
    .eq('id', progress.id);

  // Check if should advance (need to query mastery data)
  const masteryData = await getWordMasteryForSkill(childId, skillId);
  const allWords = await getWordsForSkill(skillId);
  
  const masteredCount = allWords.filter(w => masteryData.get(w.id)?.isMastered).length;
  const masteryPercentage = allWords.length > 0 ? (masteredCount / allWords.length) * 100 : 0;

  // Get skill threshold
  const skill = await getSkillById(skillId);
  const threshold = skill?.mastery_threshold || DEFAULT_MASTERY_THRESHOLD;

  return { shouldAdvance: masteryPercentage >= threshold };
}

/**
 * Get summary of skill mastery for a child
 */
export async function getSkillMasterySummary(childId: string, skillId: string): Promise<{
  totalWords: number;
  masteredWords: number;
  inProgressWords: number;
  newWords: number;
  masteryPercentage: number;
}> {
  const allWords = await getWordsForSkill(skillId);
  const masteryData = await getWordMasteryForSkill(childId, skillId);

  let masteredWords = 0;
  let inProgressWords = 0;
  let newWords = 0;

  for (const word of allWords) {
    const mastery = masteryData.get(word.id);
    if (mastery?.isMastered) {
      masteredWords++;
    } else if (mastery && mastery.productions > 0) {
      inProgressWords++;
    } else {
      newWords++;
    }
  }

  return {
    totalWords: allWords.length,
    masteredWords,
    inProgressWords,
    newWords,
    masteryPercentage: allWords.length > 0 ? (masteredWords / allWords.length) * 100 : 0,
  };
}
